import { Fragment } from 'preact';
import { useState } from 'preact/hooks';
import { useModel } from '../model.ts';
import { LANGUAGES, language } from '../../shared/languages.ts';
import { toast } from '../toast.ts';

export function Decks({ onBrowse }: { onBrowse: (deckId: string) => void }) {
  const m = useModel();
  const [editing, setEditing] = useState<string | 'new' | null>(null);
  const decks = m.liveDecks();
  const on = decks.filter((d) => d.active !== false);
  const off = decks.filter((d) => d.active === false);

  if (editing) return <DeckEdit id={editing === 'new' ? null : editing} onDone={() => setEditing(null)} />;

  return (
    <div class="decks">
      {[on, off].map((group, gi) =>
        group.length ? (
          <Fragment key={gi}>
            {gi === 1 && (
              <h3 class="muted small">Switched off: kept in your library, never studied</h3>
            )}
            <ul class={`deck-list ${gi === 1 ? 'off' : ''}`}>
              {group.map((d) => {
                const q = m.queue(d.id);
                const total = m.liveNotes().filter((n) => n.deckId === d.id).length;
                const active = d.active !== false;
                return (
                  <li key={d.id}>
                    <input
                      type="checkbox"
                      class="switch"
                      aria-label={`Study ${d.name}`}
                      title={active ? 'Switch off' : 'Switch on'}
                      checked={active}
                      onChange={(e) => {
                        void m.saveDeck({ id: d.id, active: e.currentTarget.checked });
                        toast(e.currentTarget.checked ? `${d.name} is on; its audio is being made.` : `${d.name} switched off`);
                      }}
                    />
                    <div class="deck-main" onClick={() => onBrowse(d.id)}>
                      <div class="deck-name">{d.name}</div>
                      <div class="muted small">
                        {language(d.language).name} · {total} card{total === 1 ? '' : 's'}
                      </div>
                    </div>
                    {active && (
                      <div class="counts small">
                        <span class="count new">{q.fresh.length}</span>
                        <span class="count learn">{q.learning.length + q.learnAhead.length}</span>
                        <span class="count due">{q.review.length}</span>
                      </div>
                    )}
                    <button onClick={() => setEditing(d.id)}>Edit</button>
                  </li>
                );
              })}
            </ul>
          </Fragment>
        ) : null,
      )}
      {!decks.length && <p class="center muted">No decks yet.</p>}
      <button class="primary" onClick={() => setEditing('new')}>
        + New deck
      </button>
    </div>
  );
}

function DeckEdit({ id, onDone }: { id: string | null; onDone: () => void }) {
  const m = useModel();
  const existing = id ? m.decks.get(id) : undefined;
  const [name, setName] = useState(existing?.name ?? '');
  const [lang, setLang] = useState(existing?.language ?? 'none');
  const [recognize, setRecognize] = useState(existing?.kinds.recognize ?? true);
  const [produce, setProduce] = useState(existing?.kinds.produce ?? true);
  const [newPerDay, setNewPerDay] = useState(existing?.newPerDay ?? 10);
  const [error, setError] = useState<string | null>(null);
  const audio = language(lang).voices.length > 0;

  async function save(e: Event) {
    e.preventDefault();
    if (!name.trim()) return setError('Give the deck a name.');
    if (!recognize && !produce) return setError('Turn on at least one kind of card.');
    await m.saveDeck({
      id: existing?.id,
      name: name.trim(),
      language: lang,
      kinds: { recognize, produce },
      newPerDay: Math.max(0, Math.round(newPerDay) || 0),
    });
    toast(existing ? 'Saved' : 'Deck created. Add cards under Cards.');
    onDone();
  }

  async function remove() {
    if (!existing) return;
    const n = m.liveNotes().filter((x) => x.deckId === existing.id).length;
    if (n && !confirm(`Delete "${existing.name}" and hide its ${n} cards? You can undo right after.`)) return;
    await m.saveDeck({ id: existing.id, deleted: true });
    toast('Deck deleted', { label: 'Undo', run: () => void m.saveDeck({ id: existing.id, deleted: false }) });
    onDone();
  }

  return (
    <form class="edit" onSubmit={save}>
      <h2>{existing ? 'Edit deck' : 'New deck'}</h2>
      <label>
        Name
        <input value={name} onInput={(e) => setName(e.currentTarget.value)} required />
      </label>
      <label>
        Language
        <select value={lang} onChange={(e) => setLang(e.currentTarget.value)}>
          {Object.entries(LANGUAGES).map(([key, l]) => (
            <option key={key} value={key}>
              {l.name}
            </option>
          ))}
        </select>
        <span class="muted small">
          {audio
            ? `Cards are studied by ear: ${language(lang).voices.length} voices read each card's ${language(lang).labels.text.toLowerCase()}.`
            : 'Plain text cards. You can still record audio for individual cards.'}
        </span>
      </label>
      <div class="settings">
        <label class="row">
          <span>
            {audio ? 'Listening cards' : 'Recognize cards'}{' '}
            <span class="muted small">{audio ? 'hear it, recall the meaning' : 'see the front, recall the back'}</span>
          </span>
          <input type="checkbox" checked={recognize} onChange={(e) => setRecognize(e.currentTarget.checked)} />
        </label>
        <label class="row">
          <span>
            {audio ? 'Speaking cards' : 'Recall cards'}{' '}
            <span class="muted small">{audio ? 'see the meaning, say it' : 'see the back, recall the front'}</span>
          </span>
          <input type="checkbox" checked={produce} onChange={(e) => setProduce(e.currentTarget.checked)} />
        </label>
        <label class="row">
          New cards per day
          <input
            type="number"
            min={0}
            max={1000}
            value={newPerDay}
            onInput={(e) => setNewPerDay(Number(e.currentTarget.value))}
          />
        </label>
      </div>
      {error && <p class="error">{error}</p>}
      <div class="form-actions">
        <button type="submit" class="primary">
          {existing ? 'Save' : 'Create'}
        </button>
        <button type="button" onClick={onDone}>
          Cancel
        </button>
        {existing && (
          <button type="button" class="danger" onClick={remove}>
            Delete deck
          </button>
        )}
      </div>
    </form>
  );
}
